import React, { useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { screenPanelStyles } from "./ScreenPanel";
import ADSREnvelope from "./ADSREnvelope";
import Selector from "./sequencer/components/Selector";
import { MultiKeyInstrument, getAllInstruments } from "../utils/sampleUtils";
import {
  playInstrumentOneShot,
  loadCustomSampleToInstrument,
} from "../utils/audioUtils";

interface WaveformDisplayProps {
  currentInstrument: MultiKeyInstrument;
  waveformData: number[];
  sampleDuration?: number;
  onInstrumentChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onPreviousInstrument?: () => void;
  onNextInstrument?: () => void;
  onShuffle?: () => void;
}

const WaveformDisplay: React.FC<WaveformDisplayProps> = ({
  currentInstrument,
  waveformData,
  sampleDuration = 1.0,
  onInstrumentChange,
  onPreviousInstrument,
  onNextInstrument,
  onShuffle: _onShuffle,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePlaySample = () => {
    // Play the current instrument as a one-shot
    console.log("Playing sample:", currentInstrument.name);
    playInstrumentOneShot(currentInstrument.id);
  };

  const handleFileButtonClick = () => {
    // Trigger the hidden file input when the button is clicked
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const [isLoading, setIsLoading] = useState(false);

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsLoading(true);
      console.log("Loading custom audio file:", file.name);

      // Load the custom sample
      const { instrumentId, instrumentName, detectedNote } =
        await loadCustomSampleToInstrument(file);
      console.log(
        `Custom sample "${instrumentName}" loaded with ID: ${instrumentId}${
          detectedNote ? `, detected note: ${detectedNote}` : ""
        }`
      );

      // Switch to the new instrument by creating a synthetic event
      const syntheticEvent = {
        target: { value: instrumentId },
      } as unknown as React.ChangeEvent<HTMLSelectElement>;

      // Call the onInstrumentChange prop with the synthetic event
      onInstrumentChange(syntheticEvent);

      // Reset the file input so the same file can be selected again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Show a success message including detected note if available
      if (detectedNote) {
        alert(
          `Custom sample "${instrumentName}" loaded successfully!\nDetected note: ${detectedNote}`
        );
      } else {
        alert(`Custom sample "${instrumentName}" loaded successfully!`);
      }
    } catch (error) {
      console.error("Error loading custom sample:", error);

      // Provide more specific error message
      let errorMessage = "Failed to load audio file.";
      if (error instanceof Error) {
        if (
          error.message.includes("decode") ||
          error.message.includes("content type") ||
          error.message.includes("unknown")
        ) {
          errorMessage =
            "This audio format isn't supported. Try an MP3 file or converting to MP3 format.";
        } else if (error.message.includes("buffer")) {
          errorMessage =
            "Audio buffer couldn't be processed. Try a different file format.";
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }

      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const instruments = getAllInstruments();

  return (
    <WaveformDisplayContainer id="waveform-display">
      {/* Top bar with selector and buttons */}
      <WaveformTopBar id="waveform-top-bar">
        <Selector
          id="sample-selector"
          value={currentInstrument.id}
          options={instruments.map((inst) => ({
            key: inst.id,
            label: inst.name,
          }))}
          onChange={(value) => {
            const syntheticEvent = {
              target: { value },
            } as unknown as React.ChangeEvent<HTMLSelectElement>;
            onInstrumentChange(syntheticEvent);
          }}
          onPrevious={onPreviousInstrument}
          onNext={onNextInstrument}
        />

        <TopBarButtons id="top-bar-buttons">
          {/* Hidden file input for uploading audio */}
          <HiddenFileInput
            type="file"
            ref={fileInputRef}
            accept="audio/*"
            onChange={handleFileSelected}
          />

          {/* File picker button */}
          <TopBarButton
            id="file-picker-button"
            onClick={handleFileButtonClick}
            title="Load custom audio file"
            disabled={isLoading}
            $isLoading={isLoading}
          >
            {isLoading ? (
              <Spinner width="16" height="16" viewBox="0 0 50 50">
                <SpinnerCircle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="5"
                />
              </Spinner>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
              </svg>
            )}
          </TopBarButton>

          {/* Play button */}
          <TopBarButton
            id="play-sample-button"
            onClick={handlePlaySample}
            title="Play current sample"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </TopBarButton>
        </TopBarButtons>
      </WaveformTopBar>

      <ADSRSection id="adsr-section">
        <ADSREnvelope
          waveformData={waveformData}
          sampleDuration={sampleDuration}
        />
      </ADSRSection>
    </WaveformDisplayContainer>
  );
};

export default WaveformDisplay;

const WaveformDisplayContainer = styled.div`
  ${screenPanelStyles}
  display: flex;
  flex-direction: column;
`;

const WaveformTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0px;
  gap: 12px;
  position: relative;

  @media (max-width: 768px) {
    padding: 10px 15px;
    gap: 8px;
  }
`;

const TopBarButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: absolute;
  right: 20px;
`;

const TopBarButton = styled.button<{ $isLoading?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${({ $isLoading }) =>
    $isLoading ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.4)"};
  color: #b0b0b0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.2);
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ADSRSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 15px;
  align-items: flex-start;
  justify-content: flex-start;
  margin: 0px 12px;
  position: relative;

  @media (max-width: 768px) {
    gap: 15px;
    padding: 15px;
  }
`;

const spin = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -124;
  }
`;

const Spinner = styled.svg`
  animation: ${spin} 1.5s linear infinite;
`;

const SpinnerCircle = styled.circle`
  stroke: white;
  stroke-dasharray: 150, 200;
  stroke-dashoffset: -10;
  stroke-linecap: round;
  animation: ${dash} 1.5s ease-in-out infinite;
`;
